import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { I18nProvider, useI18n } from "./i18n";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppShell } from "./components/layout/AppShell";
import Login from "./pages/Login";
import Dashboard from "./apps/staff/dashboard/Dashboard";
import CustomersList from "./apps/staff/customers/CustomersList";
import CustomerPage from "./apps/staff/customers/CustomerPage";
import Collections from "./apps/staff/collections/Collections";
import DriverRoute from "./apps/staff/route/DriverRoute";
import OfficeTasks from "./apps/staff/route/OfficeTasks";
import Reports from "./apps/staff/reports/Reports";
import HrHome from "./apps/staff/hr/HrHome";
import Budgets from "./apps/staff/finance/Budgets";
import Cash from "./apps/staff/finance/Cash";
import CustomerOnboarding from "./apps/staff/users/CustomerOnboarding";
import { CustomerLayout } from "./apps/customer/CustomerShell";
import Home from "./apps/customer/pages/Home";
import Products from "./apps/customer/pages/Products";
import ProductDetail from "./apps/customer/pages/ProductDetail";
import Cart from "./apps/customer/pages/Cart";
import Orders from "./apps/customer/pages/Orders";
import OrderDetail from "./apps/customer/pages/OrderDetail";
import Account from "./apps/customer/pages/Account";
import Chat from "./apps/customer/pages/Chat";
import Assistant from "./apps/customer/pages/Assistant";
import StaffAssistant from "./apps/staff/ai/StaffAssistant";
import Offers from "./apps/customer/pages/Offers";
import DeliveryAdmin from "./apps/staff/delivery/DeliveryAdmin";
import ContentAdmin from "./apps/staff/content/ContentAdmin";
import PromotionsAdmin from "./apps/staff/promotions/PromotionsAdmin";
import Promotion from "./apps/customer/pages/Promotion";
import SoftOneHealth from "./apps/staff/softone/SoftOneHealth";
import UsersAdmin from "./apps/staff/users/UsersAdmin";
import ForgotPassword from "./pages/ForgotPassword";
import SetPassword from "./pages/SetPassword";
import OrderQueue from "./apps/staff/orders/OrderQueue";
import QueueOrder from "./apps/staff/orders/QueueOrder";
import Warehouse from "./apps/staff/warehouse/Warehouse";
import PickingList from "./apps/staff/warehouse/PickingList";
import { ConfirmProvider } from "./components/common/ConfirmDialog";
import { hasAny, staffHome } from "./lib/perm";
import ChangePassword from "./pages/ChangePassword";
import Messages from "./apps/staff/messages/Messages";
import OrderOnBehalf from "./apps/staff/customers/OrderOnBehalf";
import Permissions from "./apps/staff/users/Permissions";
import Payments from "./apps/staff/payments/Payments";
import Maintenance from "./apps/staff/maintenance/Maintenance";
import MyAccount from "./apps/staff/account/MyAccount";
import Payroll from "./apps/staff/payroll/Payroll";
import MyPayslips from "./apps/staff/payroll/MyPayslips";
import StaffProducts from "./apps/staff/products/StaffProducts";
import StaffMenu from "./apps/staff/StaffMenu";
import StaffCategories from "./apps/staff/products/StaffCategories";
import StaffTranslations from "./apps/staff/products/StaffTranslations";
import NoPhotoReport from "./apps/staff/products/NoPhotoReport";
import Pay from "./apps/customer/pages/Pay";
import { PayProvider, PayStatus } from "./apps/customer/pages/PayStatus";

const homeFor = (user) => (user.type === "staff" ? staffHome(user) : "/app");

const RequireAuth = ({ type, perms, bare, children }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  if (user === null) return <div className="grid min-h-screen place-items-center bg-canvas text-sm text-slate-500">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ChangePassword />;
  if (type && user.type !== type) return <Navigate to={homeFor(user)} replace />;
  if (perms && !hasAny(user, perms)) return <Navigate to={homeFor(user)} replace />;
  return bare ? children : <AppShell>{children}</AppShell>;
};

const Staff = ({ perms, bare, children }) => <RequireAuth type="staff" perms={perms} bare={bare}>{children}</RequireAuth>;

const Root = () => {
  const { user } = useAuth();
  if (user === null) return null;
  return <Navigate to={user ? homeFor(user) : "/login"} replace />;
};

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <ConfirmProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/staff" element={<Root />} />
            <Route path="/staff/overview" element={<Navigate to="/staff/dashboard" replace />} />
            <Route path="/staff/dashboard" element={<Staff perms={["kpis:read", "dashboard:own"]}><Dashboard /></Staff>} />
            <Route path="/staff/customers" element={<Staff perms={["customers:read"]}><CustomersList /></Staff>} />
            <Route path="/staff/customers/:code" element={<Staff perms={["customers:read"]}><CustomerPage /></Staff>} />
            <Route path="/staff/customers/:code/order" element={<Staff perms={["orders:on_behalf"]}><OrderOnBehalf /></Staff>} />
            <Route path="/staff/permissions" element={<Staff perms={["users:manage"]}><Permissions /></Staff>} />
            <Route path="/staff/messages" element={<Staff perms={["chat:staff"]}><Messages /></Staff>} />
            <Route path="/staff/collections" element={<Staff perms={["collections:read"]}><Collections /></Staff>} />
            <Route path="/staff/route" element={<Staff perms={["deliveries:own", "deliveries:write"]}><DriverRoute /></Staff>} />
            <Route path="/staff/tasks" element={<Staff perms={["tasks:read", "deliveries:write"]}><OfficeTasks /></Staff>} />
            <Route path="/staff/reports" element={<Staff perms={["reports:view"]}><Reports /></Staff>} />
            <Route path="/staff/budgets" element={<Staff perms={["budgets:read"]}><Budgets /></Staff>} />
            <Route path="/staff/payments" element={<Staff perms={["payments:read"]}><Payments /></Staff>} />
            <Route path="/staff/maintenance" element={<Staff perms={["maint:view", "maint:manage", "maint:report"]}><Maintenance /></Staff>} />
            <Route path="/staff/cash" element={<Staff perms={["finance:cash"]}><Cash /></Staff>} />
            <Route path="/staff/orders" element={<Staff perms={["orders:review", "orders:queue"]}><OrderQueue /></Staff>} />
            <Route path="/staff/orders/:id" element={<Staff perms={["orders:review", "orders:queue"]}><QueueOrder /></Staff>} />
            <Route path="/staff/warehouse" element={<Staff perms={["orders:prepare"]}><Warehouse /></Staff>} />
            <Route path="/staff/warehouse/print" element={<Staff perms={["orders:prepare"]} bare><PickingList /></Staff>} />
            <Route path="/staff/onboarding" element={<Staff perms={["customer_users:manage"]}><CustomerOnboarding /></Staff>} />
            <Route path="/staff/users" element={<Staff perms={["customer_users:manage"]}><UsersAdmin /></Staff>} />
            <Route path="/staff/softone" element={<Staff perms={["softone:admin"]}><SoftOneHealth /></Staff>} />
            <Route path="/staff/delivery" element={<Staff perms={["deliveries:read"]}><DeliveryAdmin /></Staff>} />
            <Route path="/staff/promotions" element={<Staff perms={["content:write", "promotions:read", "offers:write", "offers:own"]}><PromotionsAdmin /></Staff>} />
            <Route path="/staff/content" element={<Staff perms={["content:write"]}><ContentAdmin /></Staff>} />
            <Route path="/staff/hr" element={<Staff perms={["hr:own"]}><HrHome /></Staff>} />
            <Route path="/staff/menu" element={<Staff perms={["hr:own"]}><StaffMenu /></Staff>} />
            <Route path="/staff/assistant" element={<Staff perms={["kpis:read", "dashboard:own"]}><StaffAssistant /></Staff>} />
            <Route path="/staff/me" element={<Staff perms={["hr:own"]}><MyAccount /></Staff>} />
            <Route path="/staff/payroll" element={<Staff perms={["payroll:manage"]}><Payroll /></Staff>} />
            <Route path="/staff/payslips" element={<Staff perms={["hr:own"]}><MyPayslips /></Staff>} />
            <Route path="/staff/products" element={<Staff perms={["catalogue:read"]}><StaffProducts /></Staff>} />
            <Route path="/staff/categories" element={<Staff perms={["catalogue:read"]}><StaffCategories /></Staff>} />
            <Route path="/staff/translations" element={<Staff perms={["softone:admin"]}><StaffTranslations /></Staff>} />
            <Route path="/staff/no-photo" element={<Staff perms={["softone:admin"]}><NoPhotoReport /></Staff>} />
            <Route path="/app" element={<RequireAuth type="customer" bare><CustomerLayout /></RequireAuth>}>
              <Route index element={<Home />} />
              <Route path="products" element={<Products />} />
              <Route path="products/:code" element={<ProductDetail />} />
              <Route path="cart" element={<Cart />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="account" element={<Account />} />
              <Route path="chat" element={<Chat />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="offers" element={<Offers />} />
              <Route path="pay" element={<Pay />} />
              <Route path="pay/status/:id" element={<PayStatus />} />
              <Route path="pay/iris/:id" element={<PayProvider kind="iris" />} />
              <Route path="pay/softone/:id" element={<PayProvider kind="softone" />} />
              <Route path="promotions/:id" element={<Promotion />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ConfirmProvider>
        <Toaster position="bottom-center" />
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
